// @ts-nocheck

/**
 * Utility function to extract attribute names from derived formula
 * @param {string} derivedFormula - The derived formula containing attribute references
 * @returns {Array} Array of attribute names found in the formula
 */
export const extractAttributeNamesFromFormula = (derivedFormula) => {
  if (!derivedFormula) {
    return [];
  }

  // Match patterns like {{attributes.attributeName}}
  const attributePattern = /\{\{attributes\.([^}]+)\}\}/g;
  const matches = [];
  let match;

  while ((match = attributePattern.exec(derivedFormula)) !== null) {
    matches.push(match[1]);
  }

  return [...new Set(matches)]; // Remove duplicates
};

/**
 * Utility function to extract all attributes from template details
 * Combines attributes from template root level and all sections
 * @param {Object} templateDetails - The template details containing all attributes
 * @returns {Array} Array of all attribute objects from template and sections
 */
export const extractAllAttributesFromTemplate = (templateDetails) => {
  const allAttributes = [];

  // Add direct template attributes (not in sections)
  if (
    templateDetails?.attributes &&
    Array.isArray(templateDetails.attributes)
  ) {
    templateDetails.attributes.forEach((attr) => {
      if (attr) {
        allAttributes.push(attr);
      }
    });
  }

  // Add attributes from all sections
  if (templateDetails?.sections && Array.isArray(templateDetails.sections)) {
    templateDetails.sections.forEach((section) => {
      if (section?.attributes && Array.isArray(section.attributes)) {
        section.attributes.forEach((attr) => {
          if (attr) {
            allAttributes.push(attr);
          }
        });
      }
    });
  }

  return allAttributes;
};

/**
 * Function to validate if attributes in derived formula exist in template
 * @param {string} derivedFormula - The derived formula
 * @param {Object} templateDetails - The template details containing all attributes
 * @returns {Object} Validation result with missing attributes object and count
 * @returns {Object} returns.missingAttributes - Object with missing attribute names as keys and "not present" as values
 * @returns {number} returns.errorCount - Number of missing attributes
 */
export const validateDerivedFormulaAttributes = (
  derivedFormula,
  allAttributeList
) => {
  const formulaAttributes = extractAttributeNamesFromFormula(derivedFormula);
  if (formulaAttributes?.length === 0) {
    return { missingAttributes: {}, errorCount: 0 };
  }

  const allTemplateAttributeNames = allAttributeList
    ?.filter((attr) => attr?.name)
    ?.map((attr) => attr?.name);

  // Find missing attributes
  const missingAttributesList = formulaAttributes?.filter(
    (attrName) => !allTemplateAttributeNames?.includes(attrName)
  );

  // Create object with missing attributes as keys and "not present" as values
  const missingAttributes = {};
  missingAttributesList?.forEach((attrName) => {
    missingAttributes[attrName] = 'not present.';
  });

  return {
    missingAttributes,
    errorCount: missingAttributesList?.length,
  };
};

/**
 * Returns autofilled attribute values based on priority:
 * 1. User-entered / baseAttributeData
 * 2. attributeDetails (system defaults)
 */
export function getAutoFillAttributes({
  autoFillAttributeIds = [],
  baseAttributeData = {},
  attributeDetails = {},
}) {
  const result = {};

  autoFillAttributeIds.forEach((attrId) => {
    if (attrId in baseAttributeData) {
      result[attrId] = baseAttributeData[attrId];
      return;
    }

    // autofill from attributeDetails
    if (attrId in attributeDetails) {
      result[attrId] = attributeDetails[attrId];
    }
  });

  return result;
}
